/**
 * EnrollmentFormModal Component
 * Modal for creating and editing enrollments (Admin only)
 */

import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Search } from "lucide-react";
import { motion } from "framer-motion";
import enrollmentsService from "../../api/services/enrollments.service";
import classesService from "../../api/services/classes.service";
import childrenService from "../../api/services/children.service";
import toast from "react-hot-toast";

const STATUS_OPTIONS = [
  { id: "active", name: "Active" },
  { id: "pending", name: "Pending" },
  { id: "completed", name: "Completed" },
  { id: "cancelled", name: "Cancelled" },
  { id: "waitlisted", name: "Waitlisted" },
  // Backend status_map already accepts "paused"; the form never offered it,
  // so an admin could not set or clear a pause from the UI.
  { id: "paused", name: "Paused" },
];

function CustomDropdown({
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
  // When given, typing asks the parent to search (on the server) instead of
  // filtering the loaded options, which can be a small slice of the total.
  onSearch,
  // Name to show for `value` when it is not among the current options.
  selectedLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const updateSearch = (term) => {
    setSearchTerm(term);
    if (onSearch) onSearch(term);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        if (onSearch) onSearch("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onSearch]);

  const selectedOption = options.find((opt) => opt.id === value);
  const displayText = selectedOption
    ? selectedOption.name
    : (value && selectedLabel) || placeholder;
  const showsSelection = Boolean(selectedOption || (value && selectedLabel));

  const filteredOptions = onSearch
    ? options
    : options.filter((opt) =>
        opt.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold flex items-center justify-between bg-white transition-colors ${
          error ? "border-red-500" : "border-border-light"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        style={{ boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.05)" }}
      >
        <span
          className={`text-sm truncate ${
            showsSelection ? "text-gray-900" : "text-gray-400"
          }`}
        >
          {displayText}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        </motion.div>
      </button>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{
          opacity: isOpen ? 1 : 0,
          y: isOpen ? 0 : -10,
          pointerEvents: isOpen ? "auto" : "none",
        }}
        transition={{ duration: 0.2 }}
        className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg z-50"
      >
        {(onSearch || options.length > 5) && (
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => updateSearch(e.target.value)}
                placeholder={onSearch ? "Search by name..." : "Search..."}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-gold"
              />
            </div>
          </div>
        )}
        <div className="max-h-48 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No options found
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id, option);
                  setIsOpen(false);
                  updateSearch("");
                }}
                className={`w-full px-4 py-2 text-left hover:bg-btn-gold hover:text-heading-dark transition-colors text-sm ${
                  value === option.id
                    ? "bg-btn-gold text-text-primary"
                    : "text-heading-dark"
                }`}
              >
                {option.name}
              </button>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function EnrollmentFormModal({
  isOpen,
  onClose,
  mode = "create",
  initialData = null,
  onSuccess,
}) {
  const [formData, setFormData] = useState({
    child_id: "",
    class_id: "",
    status: "active",
    base_price: "",
    discount_amount: "0",
    final_price: "",
    classroom_teacher: "",
    auto_charge: false,
    send_payment_link: false,
  });
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [classes, setClasses] = useState([]);
  const [children, setChildren] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  // The picked child's name, kept when a later search no longer lists them.
  const [selectedChildName, setSelectedChildName] = useState("");
  const [childSearch, setChildSearch] = useState("");
  // Only a failed first load falls back to typing a child ID by hand.
  const [childListFailed, setChildListFailed] = useState(false);
  const childRequestRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();

      if (mode === "edit" && initialData) {
        setFormData({
          child_id: initialData.child_id || "",
          class_id: initialData.class_id || "",
          status: initialData.status || "active",
          base_price: initialData.base_price?.toString() || "",
          discount_amount: initialData.discount_amount?.toString() || "0",
          final_price: initialData.final_price?.toString() || "",
          classroom_teacher: initialData.classroom_teacher || "",
          auto_charge: false,
          send_payment_link: false,
        });
      } else {
        // Creating. `initialData` may still carry a class, so staff can open
        // this straight from a class page with that class already chosen.
        // It may also carry the child, when staff start from "Add Class"
        // on a family's child.
        setFormData({
          child_id: initialData?.child_id || "",
          class_id: initialData?.class_id || "",
          status: "active",
          base_price: "",
          discount_amount: "0",
          final_price: "",
          classroom_teacher: "",
          auto_charge: false,
          send_payment_link: false,
        });
      }
      setPaymentMethod(null);
      setErrors({});
      setChildSearch("");
      setSelectedChildName("");

      // The child is rarely among the first children loaded, so look the
      // name up directly rather than waiting to find it in the list.
      const preselectedChildId = initialData?.child_id;
      if (preselectedChildId) {
        // A failed name lookup must never take the form down with it.
        (async () => {
          try {
            const child = await childrenService.getById(preselectedChildId);
            if (child) {
              setSelectedChildName(`${child.first_name} ${child.last_name}`);
            }
          } catch (error) {
            console.warn("Could not load child:", error);
          }
        })();
        if (mode !== "edit") fetchPaymentMethod(preselectedChildId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, initialData]);

  // Children are searched on the server: there are thousands, and the form
  // used to filter only the newest 200 in the browser, so most families
  // could never be found. Only the latest request may update the list.
  useEffect(() => {
    if (!isOpen) return undefined;
    const term = childSearch.trim();
    const timer = setTimeout(
      async () => {
        const requestId = ++childRequestRef.current;
        const params = { limit: 50 };
        if (term) params.search = term;
        try {
          const res = await childrenService.getAll(params);
          if (requestId !== childRequestRef.current) return;
          setChildren(
            (res.items || []).map((c) => ({
              id: c.id,
              name: `${c.first_name} ${c.last_name}`,
            })),
          );
          setChildListFailed(false);
        } catch (error) {
          console.warn("Could not fetch children list:", error);
          if (requestId === childRequestRef.current && !term) {
            setChildListFailed(true);
          }
        }
      },
      term ? 300 : 0,
    );
    return () => clearTimeout(timer);
  }, [isOpen, childSearch]);

  const fetchDropdownData = async () => {
    setLoadingData(true);
    try {
      // Fetch classes (required)
      const classesRes = await classesService.getAll({ limit: 200 });
      setClasses(
        (classesRes.items || []).map((c) => ({
          id: c.id,
          name: `${c.name} (${c.current_enrollment || 0}/${c.capacity || 0})`,
          base_price: c.base_price,
        })),
      );
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error);
      toast.error("Failed to load form data");
    } finally {
      setLoadingData(false);
    }
  };

  // Fetch parent's payment method when child is selected
  const fetchPaymentMethod = async (childId) => {
    if (!childId) {
      setPaymentMethod(null);
      return;
    }
    setLoadingPaymentMethod(true);
    try {
      const result = await enrollmentsService.getParentPaymentMethod(childId);
      setPaymentMethod(result);
      // No card on file: billing them is the norm, so the link starts ticked.
      setFormData((prev) => ({ ...prev, send_payment_link: !result }));
    } catch (error) {
      console.warn("Could not fetch payment method:", error);
      setPaymentMethod(null);
      // Unknown, not "no card": offer the link but leave it to staff.
      setFormData((prev) => ({ ...prev, send_payment_link: false }));
    } finally {
      setLoadingPaymentMethod(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-calculate final price
      if (field === "base_price" || field === "discount_amount") {
        const base = parseFloat(updated.base_price) || 0;
        const discount = parseFloat(updated.discount_amount) || 0;
        updated.final_price = Math.max(0, base - discount).toFixed(2);
      }

      // Auto-fill base_price when class is selected
      if (field === "class_id" && value) {
        const selectedClass = classes.find((c) => c.id === value);
        if (selectedClass && selectedClass.base_price) {
          updated.base_price = selectedClass.base_price.toString();
          const discount = parseFloat(updated.discount_amount) || 0;
          updated.final_price = Math.max(
            0,
            parseFloat(selectedClass.base_price) - discount,
          ).toFixed(2);
        }
      }

      // Fetch payment method when child is selected
      if (field === "child_id" && value) {
        fetchPaymentMethod(value);
      }

      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.child_id?.trim()) {
      newErrors.child_id = "Child is required";
    }

    if (!formData.class_id?.trim()) {
      newErrors.class_id = "Class is required";
    }

    if (!formData.status) {
      newErrors.status = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setIsSubmitting(true);

    try {
      let submitData;

      if (mode === "create") {
        // Create: include child_id and class_id
        submitData = {
          child_id: formData.child_id.trim(),
          class_id: formData.class_id.trim(),
          status: formData.status,
        };

        if (formData.base_price) {
          submitData.base_price = parseFloat(formData.base_price);
        }
        if (formData.discount_amount) {
          submitData.discount_amount = parseFloat(formData.discount_amount);
        }
        if (formData.final_price) {
          submitData.final_price = parseFloat(formData.final_price);
        }

        if (formData.classroom_teacher?.trim()) {
          submitData.classroom_teacher = formData.classroom_teacher.trim();
        }

        // The server charges the card by the class's own billing: a monthly
        // class starts its subscription (this month's share plus required
        // fees), a one-time class is charged its price. A failed charge
        // enrolls nobody and comes back as an error.
        const charge = formData.auto_charge && paymentMethod;
        if (charge) submitData.auto_charge = true;

        // No card: create it PENDING (holds no seat) and email a checkout
        // link. Stripe's webhook makes it ACTIVE once the parent pays.
        const sendLink = formData.send_payment_link && !paymentMethod;
        if (sendLink) submitData.status = "pending";

        const created = await enrollmentsService.create(submitData);

        if (sendLink) {
          try {
            const link = await enrollmentsService.sendPaymentLink(created.id);
            toast.success(
              `Enrollment created as Pending. Payment link emailed to ${
                link?.parent_email || "the parent"
              }`,
            );
          } catch (linkError) {
            console.error("Failed to send payment link:", linkError);
            toast.error(
              `Enrollment created as Pending, but the payment link was not ` +
                `sent (${linkError.message || "unknown error"}). Use ` +
                `"Send Payment Link" on the Enrollments page to retry.`,
            );
          }
        } else {
          toast.success(
            charge
              ? "Enrollment created and the parent's card charged"
              : "Enrollment created successfully",
          );
        }
      } else {
        // Update: only send status and pricing fields
        submitData = {
          status: formData.status,
        };

        if (formData.base_price) {
          submitData.base_price = parseFloat(formData.base_price);
        }
        if (
          formData.discount_amount !== undefined &&
          formData.discount_amount !== ""
        ) {
          submitData.discount_amount = parseFloat(formData.discount_amount);
        }
        if (formData.final_price) {
          submitData.final_price = parseFloat(formData.final_price);
        }

        // Sent unconditionally: an empty string is how an admin clears the
        // teacher, and omitting the key would make that a silent no-op.
        submitData.classroom_teacher = formData.classroom_teacher.trim();

        await enrollmentsService.update(initialData.id, submitData);
        toast.success("Enrollment updated successfully");
      }

      onSuccess?.();
    } catch (error) {
      console.error("Failed to save enrollment:", error);
      const errorMessage =
        error.message ||
        error.response?.data?.message ||
        `Failed to ${mode === "create" ? "create" : "update"} enrollment`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold font-kollektif text-text-primary">
            {mode === "create" ? "Create Enrollment" : "Edit Enrollment"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-btn-gold"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Child Selection */}
              <div>
                <label className="block text-sm font-semibold font-manrope text-text-primary mb-1">
                  Child <span className="text-red-500">*</span>
                </label>
                {!childListFailed ? (
                  <CustomDropdown
                    value={formData.child_id}
                    onChange={(value, option) => {
                      if (option) setSelectedChildName(option.name);
                      updateField("child_id", value);
                    }}
                    options={children}
                    onSearch={setChildSearch}
                    selectedLabel={selectedChildName}
                    placeholder="Select child"
                    error={errors.child_id}
                    disabled={mode === "edit"}
                  />
                ) : (
                  <input
                    type="text"
                    value={formData.child_id}
                    onChange={(e) => updateField("child_id", e.target.value)}
                    disabled={mode === "edit"}
                    className={`w-full px-3 py-2 border rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold ${
                      errors.child_id ? "border-red-500" : "border-border-light"
                    } ${mode === "edit" ? "opacity-50 cursor-not-allowed" : ""}`}
                    placeholder="Enter child ID"
                  />
                )}
                {errors.child_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.child_id}</p>
                )}
              </div>

              {/* Class Selection */}
              <div>
                <label className="block text-sm font-semibold font-manrope text-text-primary mb-1">
                  Class <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  value={formData.class_id}
                  onChange={(value) => updateField("class_id", value)}
                  options={classes}
                  placeholder="Select class"
                  error={errors.class_id}
                  disabled={mode === "edit"}
                />
                {errors.class_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.class_id}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold font-manrope text-text-primary mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  value={formData.status}
                  onChange={(value) => updateField("status", value)}
                  options={STATUS_OPTIONS}
                  placeholder="Select status"
                  error={errors.status}
                />
                {errors.status && (
                  <p className="text-red-500 text-xs mt-1">{errors.status}</p>
                )}
              </div>

              {/* Pricing Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-semibold font-manrope text-text-primary mb-3">
                  Pricing (Optional)
                </h3>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium font-manrope text-gray-600 mb-1">
                      Base Price
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.base_price}
                        onChange={(e) =>
                          updateField("base_price", e.target.value)
                        }
                        className="w-full pl-7 pr-3 py-2 border border-border-light rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium font-manrope text-gray-600 mb-1">
                      Discount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.discount_amount}
                        onChange={(e) =>
                          updateField("discount_amount", e.target.value)
                        }
                        className="w-full pl-7 pr-3 py-2 border border-border-light rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium font-manrope text-gray-600 mb-1">
                      Final Price
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.final_price}
                        onChange={(e) =>
                          updateField("final_price", e.target.value)
                        }
                        className="w-full pl-7 pr-3 py-2 border border-border-light rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Classroom teacher. Parents fill this in at checkout but
                    routinely leave it blank or guess, and school rosters are
                    organised by teacher — so admins need it editable after
                    the fact, not just at registration. */}
                <div className="mt-4">
                  <label className="block text-xs font-medium font-manrope text-gray-600 mb-1">
                    Classroom Teacher
                  </label>
                  <input
                    type="text"
                    value={formData.classroom_teacher}
                    onChange={(e) =>
                      updateField("classroom_teacher", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-border-light rounded-[12px] font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold text-sm"
                    placeholder="e.g. Mrs. Johnson"
                    maxLength={255}
                  />
                  <p className="mt-1 text-xs text-gray-500 font-manrope">
                    Shown on the class roster. Leave blank to clear.
                  </p>
                </div>
              </div>

              {/* Auto-Charge Section (Create mode only) */}
              {mode === "create" && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold font-manrope text-text-primary mb-3">
                    Payment
                  </h3>

                  {/* Payment Method Display */}
                  {loadingPaymentMethod && (
                    <div className="text-sm text-gray-500 mb-3">
                      Loading parent's payment method...
                    </div>
                  )}

                  {!loadingPaymentMethod && formData.child_id && (
                    <div className="mb-3">
                      {paymentMethod ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span className="text-sm font-medium text-green-800">
                              Default Payment Method:
                            </span>
                            <span className="text-sm text-green-700">
                              {paymentMethod.brand} •••• {paymentMethod.last4}
                              {paymentMethod.exp_month &&
                                paymentMethod.exp_year && (
                                  <span className="text-gray-500 ml-2">
                                    (Exp: {paymentMethod.exp_month}/
                                    {paymentMethod.exp_year})
                                  </span>
                                )}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-5 h-5 text-yellow-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <span className="text-sm text-yellow-800">
                              No default payment method found. Auto-charge not
                              available.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Auto-charge toggle */}
                  <label className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={formData.auto_charge}
                      onChange={(e) =>
                        updateField("auto_charge", e.target.checked)
                      }
                      disabled={!paymentMethod}
                      className="w-4 h-4 text-btn-gold border-gray-300 rounded focus:ring-btn-gold disabled:opacity-50"
                    />
                    <span
                      className={`text-sm font-medium ${!paymentMethod ? "text-gray-400" : "text-gray-700"}`}
                    >
                      Automatically charge parent's default payment method
                    </span>
                  </label>

                  {formData.auto_charge && (
                    <p className="text-xs text-gray-500">
                      Charged when you save, the same way as checkout: a
                      monthly class starts its subscription with this
                      month&apos;s share and any required fees; a one-time
                      class is charged its price. If the card is declined,
                      the child is not enrolled.
                    </p>
                  )}

                  {/* No card on file: email a checkout link instead */}
                  {!loadingPaymentMethod &&
                    formData.child_id &&
                    !paymentMethod && (
                      <>
                        <label className="flex items-center gap-3 mb-3">
                          <input
                            type="checkbox"
                            checked={formData.send_payment_link}
                            onChange={(e) =>
                              updateField("send_payment_link", e.target.checked)
                            }
                            className="w-4 h-4 text-btn-gold border-gray-300 rounded focus:ring-btn-gold"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Email the parent a payment link
                          </span>
                        </label>
                        {formData.send_payment_link && (
                          <p className="text-xs text-gray-500">
                            The enrollment is created as Pending and does not
                            hold a seat. The parent gets an email to pay, priced
                            the same way as checkout. It becomes Active once
                            they pay. The link expires after 24 hours; resend
                            it from the Enrollments page if needed.
                          </p>
                        )}
                      </>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold font-manrope text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loadingData}
              className="px-6 py-2 text-sm font-semibold font-manrope text-text-body bg-btn-gold rounded-lg hover:bg-btn-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? "Saving..."
                : mode === "create"
                  ? "Create Enrollment"
                  : "Update Enrollment"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
